import { useMemo } from "react";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

const MONTHS = [
  "January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December",
];

export type DOBValue = {
  year: number | null;
  month: number | null; // 1-12
  day: number | null;
};

export function dobToDate(v: DOBValue): Date | null {
  if (!v.year || !v.month || !v.day) return null;
  const d = new Date(v.year, v.month - 1, v.day);
  if (
    d.getFullYear() !== v.year ||
    d.getMonth() !== v.month - 1 ||
    d.getDate() !== v.day
  ) {
    return null;
  }
  return d;
}

function daysInMonth(year: number | null, month: number | null) {
  if (!year || !month) return 31;
  return new Date(year, month, 0).getDate();
}

interface Props {
  value: DOBValue;
  onChange: (v: DOBValue) => void;
  id?: string;
}

export function DateOfBirthPicker({ value, onChange, id }: Props) {
  const currentYear = new Date().getFullYear();
  const years = useMemo(
    () => Array.from({ length: 100 }, (_, i) => currentYear - i),
    [currentYear],
  );
  const days = useMemo(
    () =>
      Array.from(
        { length: daysInMonth(value.year, value.month) },
        (_, i) => i + 1,
      ),
    [value.year, value.month],
  );

  return (
    <div id={id} className="grid grid-cols-3 gap-2">
      <Select
        value={value.month ? String(value.month) : ""}
        onValueChange={(v) => onChange({ ...value, month: Number(v) })}
      >
        <SelectTrigger aria-label="Birth month">
          <SelectValue placeholder="Month" />
        </SelectTrigger>
        <SelectContent className="max-h-64">
          {MONTHS.map((m, i) => (
            <SelectItem key={m} value={String(i + 1)}>
              {m}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>

      <Select
        value={value.day ? String(value.day) : ""}
        onValueChange={(v) => onChange({ ...value, day: Number(v) })}
      >
        <SelectTrigger aria-label="Birth day">
          <SelectValue placeholder="Day" />
        </SelectTrigger>
        <SelectContent className="max-h-64">
          {days.map((d) => (
            <SelectItem key={d} value={String(d)}>
              {d}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>

      <Select
        value={value.year ? String(value.year) : ""}
        onValueChange={(v) => onChange({ ...value, year: Number(v) })}
      >
        <SelectTrigger aria-label="Birth year">
          <SelectValue placeholder="Year" />
        </SelectTrigger>
        <SelectContent className="max-h-64">
          {years.map((y) => (
            <SelectItem key={y} value={String(y)}>
              {y}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  );
}

export function calculateAge(date: Date, now: Date = new Date()): number {
  let age = now.getFullYear() - date.getFullYear();
  const m = now.getMonth() - date.getMonth();
  if (m < 0 || (m === 0 && now.getDate() < date.getDate())) age--;
  return age;
}
